import { Badge, Button, Group, Paper, Select, Stack, Text, Title } from '@mantine/core';
import { IconGift, IconSparkles, IconBuildingStore } from '@tabler/icons-react';
import { useState } from 'react';
import {
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  GAME_CARD_REASON_LABELS,
  isPlayerKind,
  type GameSessionView,
  type ResourceKey,
} from '@creativitas/shared';

export type HostActions = {
  transfer?: (fromParticipantId: string, toParticipantId: string, resource: ResourceKey) => void;
  bazaar?: (participantId: string, resource: ResourceKey) => void;
  grantedTalent?: (participantId: string) => void;
};

/**
 * The two things the facilitator does for players rather than to a counter
 * (GAME-35, GAME-36).
 *
 * Both are conversations that happen out loud first: somebody offers a resource
 * to somebody else, or a player standing in the Bazaar decides to trade. The
 * facilitator then records the outcome — which is why these are commands here
 * and not buttons in front of the players.
 */
export function HostActionsPanel({
  session,
  busy,
  actions,
}: {
  session: GameSessionView;
  busy: boolean;
  actions: HostActions;
}) {
  const players = session.participants.filter(
    (participant) => isPlayerKind(participant.kind) && !participant.finished,
  );
  const active = session.status === 'active' || session.status === 'paused';
  if (!active || players.length === 0) return null;

  return (
    <Stack gap="md">
      <GiftControl players={players} busy={busy} actions={actions} />
      <BazaarControl session={session} busy={busy} actions={actions} />
    </Stack>
  );
}

function GiftControl({
  players,
  busy,
  actions,
}: {
  players: GameSessionView['participants'];
  busy: boolean;
  actions: HostActions;
}) {
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [resource, setResource] = useState<ResourceKey>('presence');

  if (!actions.transfer || players.length < 2) return null;
  const sender = players.find((player) => player.id === from);
  const held = sender?.resources?.[resource] ?? 0;
  const ready = Boolean(from && to && from !== to && held > 0);

  return (
    <Paper withBorder p="md" data-testid="gift-resource">
      <Group gap="xs" mb="xs">
        <IconGift size={18} color="var(--accent)" />
        <Title order={4}>Подарить ресурс</Title>
      </Group>
      <Text size="sm" c="var(--text-muted)" mb="sm">
        Участники дарят ресурсы друг другу голосом — ведущий переносит фишку.
      </Text>
      <Group gap="xs" align="flex-end" wrap="wrap">
        <Select
          size="xs"
          w={150}
          label="От кого"
          placeholder="Участник"
          data={players.map((player) => ({ value: player.id, label: player.displayName }))}
          value={from}
          onChange={setFrom}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          size="xs"
          w={150}
          label="Кому"
          placeholder="Участник"
          data={players
            .filter((player) => player.id !== from)
            .map((player) => ({ value: player.id, label: player.displayName }))}
          value={to}
          onChange={setTo}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          size="xs"
          w={160}
          label="Ресурс"
          data={RESOURCE_KEYS.map((key) => ({ value: key, label: RESOURCE_LABELS[key] }))}
          value={resource}
          onChange={(next) => setResource((next as ResourceKey) ?? 'presence')}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
        <Button
          size="xs"
          disabled={!ready || busy}
          onClick={() => {
            if (!from || !to) return;
            actions.transfer?.(from, to, resource);
            setFrom(null);
            setTo(null);
          }}
        >
          Передать
        </Button>
      </Group>
      {/*
        Saying why the button is off beats a button that gets refused: the
        server will not let a player give away what they do not hold.
      */}
      {from && held === 0 ? (
        <Text size="xs" c="var(--text-subtle)" mt={6}>
          У участника нет ресурса «{RESOURCE_LABELS[resource]}».
        </Text>
      ) : null}
    </Paper>
  );
}

function BazaarControl({
  session,
  busy,
  actions,
}: {
  session: GameSessionView;
  busy: boolean;
  actions: HostActions;
}) {
  const [resource, setResource] = useState<ResourceKey>('money_bag');
  const [participant, setParticipant] = useState<string | null>(null);

  const inBazaar = session.participants.filter(
    (item) => session.bazaarParticipantIds.includes(item.id),
  );
  const grants = session.talentGrants;
  if (!actions.bazaar && grants.length === 0) return null;
  if (inBazaar.length === 0 && grants.length === 0) return null;

  const chosen = inBazaar.find((item) => item.id === participant) ?? inBazaar[0];
  const held = chosen?.resources?.[resource] ?? 0;

  return (
    <Paper withBorder p="md" data-testid="bazaar-exchange">
      <Group gap="xs" mb="xs">
        <IconBuildingStore size={18} color="var(--accent)" />
        <Title order={4}>Восточный базар</Title>
      </Group>

      {inBazaar.length > 0 && actions.bazaar ? (
        <>
          <Text size="sm" c="var(--text-muted)" mb="sm">
            Пока участник в секторе, он может обменять любой ресурс на карту Таланта.
            Обмен можно повторять.
          </Text>
          <Group gap="xs" align="flex-end" wrap="wrap">
            <Select
              size="xs"
              w={170}
              label="Участник в секторе"
              data={inBazaar.map((item) => ({ value: item.id, label: item.displayName }))}
              value={chosen?.id ?? null}
              onChange={setParticipant}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
            />
            <Select
              size="xs"
              w={160}
              label="Меняем"
              data={RESOURCE_KEYS.map((key) => ({ value: key, label: RESOURCE_LABELS[key] }))}
              value={resource}
              onChange={(next) => setResource((next as ResourceKey) ?? 'money_bag')}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
            />
            <Button
              size="xs"
              color="gold"
              disabled={!chosen || held < 1 || busy}
              onClick={() => chosen && actions.bazaar?.(chosen.id, resource)}
            >
              Обменять на Талант
            </Button>
          </Group>
          {chosen && held === 0 ? (
            <Text size="xs" c="var(--text-subtle)" mt={6}>
              У участника нет ресурса «{RESOURCE_LABELS[resource]}».
            </Text>
          ) : null}
          {/*
            Числовой баланс сюда не входит: обменивается ли он — открытый вопрос
            к Дарье, и подставлять за неё правило нельзя.
          */}
          <Text size="xs" c="var(--text-subtle)" mt={6}>
            Денежный баланс в обмен не входит — правило не подтверждено.
          </Text>
        </>
      ) : null}

      {grants.length > 0 ? (
        <Stack gap={6} mt={inBazaar.length > 0 ? 'md' : 0}>
          <Group gap="xs">
            <IconSparkles size={16} color="var(--gold-deep)" />
            <Text size="sm" fw={600}>Право на карту Таланта</Text>
            <Badge size="sm" color="gold" variant="light">{grants.length}</Badge>
          </Group>
          <Group gap="xs" wrap="wrap">
            {grants.map((grant) => {
              const owner = session.participants.find((item) => item.id === grant.participantId);
              return (
                <Button
                  key={grant.id}
                  size="compact-xs"
                  variant="light"
                  color="gold"
                  disabled={busy || !actions.grantedTalent}
                  onClick={() => actions.grantedTalent?.(grant.participantId)}
                  title={GAME_CARD_REASON_LABELS[grant.reason]}
                >
                  {owner?.displayName ?? 'Участник'} — вытянуть
                </Button>
              );
            })}
          </Group>
        </Stack>
      ) : null}
    </Paper>
  );
}
